import type { CoreEnv } from '@eventbrite/legacy-infra.context-types';
import { type MessageDescriptor } from 'react-intl';
import type { CamelCaseLocation } from '@eventbrite/consumer-search.search-bar/types/location';
import type { CompletionType } from '@eventbrite/consumer-search.search-bar/constants';

export type NavItemType = DropdownContainerProps | navigationLinkProps;
export interface DropdownProps {
    dropdownItems: DropdownItem[];
}

export interface DropdownContainerProps {
    dropdownItems: DropdownItem[];
    heapIdentifier: string;
    translatedText: MessageDescriptor;
}

export enum IntentToggleOptions {
    FIND_EVENTS = 'find-events',
    HOST_EVENTS = 'host-events',
}

export interface AttendeeCreatorToggleProps {
    activeOption?: IntentToggleOptions;
    hostEventsRedirectURL?: string;
}

export interface IntentToggleProps {
    show?: boolean;
    activeOption: IntentToggleOptions;
    hostEventsRedirectURL?: string;
}

export interface GlobalNavProps {
    positionSticky?: positionStickyProps;
    searchBar?: searchBarProps;
    logo?: LogoProps;
    navigation?: navigationProps;
    user?: UserShape;
    env: Partial<CoreEnv>;
    intentToggle?: IntentToggleProps;
}

export interface GlobalNavContextProps {
    positionSticky?: positionStickyProps;
    searchBar?: searchBarProps;
    logo?: LogoProps;
    navigation?: navigationProps;
    user?: UserShape; //TODO: confirm what do we use UserShape or CoreUser
    env?: Partial<CoreEnv>;
    intentToggle?: IntentToggleProps;
}

export interface dropdownItemsProps {
    path?: string;
    // text to display
    heapIdentifier: string;
    /** Text to display*/
    translatedText: MessageDescriptor;
    // text color
    textColor?: string;
    // text decoration
    textDecoration?: string;
    // Icon to display next to the link
    iconType?: React.ReactNode;
    // position icon to the left or right of the dropdown
    iconPosition?: 'left' | 'right';
    nestedDropdownItems?: navigationLinkProps[];
}

export interface navigationDropdownProps {
    // heapIdentifier
    heapIdentifier: string;
    // text color
    textColor?: string;
    // text decoration
    textDecoration?: string;
    // Icon to display next to the link
    iconType?: React.ReactNode;
    // position icon to the left or right of the dropdown
    iconPosition?: 'left' | 'right';
    dropdownItems?: dropdownItemsProps[];
}
export interface navigationLinkProps {
    /** This is URL path will be appended to the current users domain (serverURL) */
    path: string;
    /** heapIdentifier*/
    heapIdentifier: string;
    /** Text to display*/
    translatedText: MessageDescriptor;
    /** Text color*/
    textColor?: string;
    /** Icon to display next to the link*/
    iconType?: React.ReactNode;
    /** Position icon to the left or right of the link*/
    iconPosition?: 'left' | 'right';
    /** Classname to allow user more customization*/
    className?: string;
}

export interface navigationProps {
    // Desktop NavItems
    desktop: NavItemType[];
    // Mobile NavItems
    mobile: NavItemType[];
}

export interface searchBarProps {
    renderSearchBar?: boolean;
    desktopLabel?: MessageDescriptor | string;
    mobileLabel?: MessageDescriptor | string;
    searchQuery?: string;
    location?: CamelCaseLocation;
    isUsingCurrentLocation?: boolean;
    chatbotEnabled?: boolean;
    completionTypes?: CompletionType[];
    onAutocompleteCallback?: () => void;
    pageType?: string;
}
export interface MobileLogoProps {
    iconType?: JSX.Element;
    iconWidth?: string;
    iconHeight?: string;
    iconColor?: string;
    iconTitle?: string;
    backgroundColor?: string;
}
export interface DesktopLogoProps {
    iconType?: JSX.Element;
    iconWidth?: string;
    iconHeight?: string;
    iconColor?: string;
    iconTitle?: string;
    backgroundColor?: string;
}

export interface LogoProps {
    path: string;
    ariaLabel?: MessageDescriptor;
    mobileLogo?: MobileLogoProps;
    desktopLogo?: DesktopLogoProps;
}

export interface positionStickyProps {
    mobile?: boolean;
    desktop?: boolean;
}

export interface DropdownItem {
    /**  heapIdentifier that will appear in the dropdown item*/
    heapIdentifier: string;
    /**  Text that will appear in the dropdown item*/
    translatedText: MessageDescriptor;
    /** The color of the text string to be injected directly into the CSS styles  */
    textColor?: string;
    /** Optional classnames attribute to set your own custom styles on the dropdown list item */
    className?: string;
    /**
     * The path that the link will redirect to
     *
     * This should be appended by a trailing slash to avoid an unecessary redirect
     *
     * ✅ /l/sell-tickets/
     *
     * ❌ /l/sell-tickets <-- missing trailing slash
     *
     * If this is a nested dropdown, no path is required
     * */
    path?: string | URL;
    /**
     * Icons can be found in
     * "@eventbrite/marmalade-icon", "@eventbrite/eds-icon", "@eventbrite/lean-eds"
     */
    iconType?: React.ReactNode;
    /**
     * Determines if the icon renders to the left or right of the dropdown text
     */
    iconPosition?: 'left' | 'right';
    /** List of subitems to be rendered in a nested dropdown
     *
     * ⚠️ This is only available in mobile drodpowns. Desktop dropdowns do not currently support nesting
     */
    dropdownItems?: DropdownItem[];
}

export interface NestedDropdownItemProps {
    heapIdentifier: string;
    translatedText: MessageDescriptor;
    path?: string | URL;
    iconType?: React.ReactNode;
    dropdownItems: DropdownItem[];
}

//copy over from https://github.com/eventbrite/eb-ui/blob/fe3742759d13cde758f2682f35460e4c7f51d3b3/packages/infra/global-nav/src/modules/common/components/SearchBar/types/index.ts
export type UserShape = {
    canInvite?: boolean;
    canCreateEvents?: boolean;
    firstName?: string;
    fullName?: string;
    hasEvents?: boolean;
    hasOrganizers?: boolean;
    hasTikibar?: boolean;
    isAttendee?: boolean;
    isAuthenticated?: boolean;
    isStaff?: boolean;
    numOrders?: number;
    numSavedEvents?: number;
    publicId?: string;
    orgHasEvents?: boolean;
    thumbnailUrl?: string;
    numFollowing?: number;
    email?: string;
};

export type RequestShape = {
    path?: string;
    session_id?: string;
};

export type FeatureFlags = {
    showReportBugLinkInHeader?: boolean;
    useCurrentLocation?: boolean;
    showHelpCenterMenu?: boolean;
};

export type MenuInfoShape = {
    textItems?: {
        value: string;
        content: JSX.Element;
    }[];
    iconType?: React.ReactNode;
    iconColor?: string;
    narrowIconType?: React.ReactNode;
    narrowIconColor?: string;
    label?: string;
    fallbackUrl: string;
    navItems?: React.ReactNode;
    className?: Function | string;
    preventMouseOver?: boolean | undefined;
    imageUrl?: string;
};
